import React, { useState } from 'react';
import { ModalBody, ModalHeader } from 'reactstrap';
import Rodal from '../../../../components/Rodal/Rodal';
import ButtonLoader from '../../../../components/Layout/Buttons/ButtonLoader';
import { LoiComparisonFormDto } from '../../../../slices/marketplace/loi-comparison/models/loi-comparison-form.model';
import { LevelOfService } from '../../../../slices/marketplace/timeline/models/enums/level-of-service.enum';
import { selectAndExecuteLoi } from '../../../../slices/marketplace/loi-comparison/loi-comparison-api';
import {
    buildCashAtCloseRow,
    buildCorporateEquityRow,
    buildDeferredFixedRow,
    buildDeferredVariableRow,
    buildEarnoutRow,
    buildJvEquityRow,
    buildOtherRow,
    buildSyntheticJvRow,
    computeTotalValues,
    formatCurrency,
    formatCurrencyAllowZero,
    RowCellValues,
} from '../LoiComparisonSummary/LoiComparisonRows';
import { useStateSelector } from '../../../../store/selectors';
import createNotification from '../../../../utils/createNotification';
import './SelectExecuteLoiPopup.scss';

const POPUP_WIDTH = 750;

type PopupStage = 'confirming' | 'success';

export interface SelectExecuteLoiPopupProps {
    isVisible: boolean;
    onClose: () => void;
    item: LoiComparisonFormDto;
    levelOfService: number;
    engagementId: number;
    eventId: string;
    onSuccess: () => void;
}

interface PriceSummaryRowData {
    label: string;
    cells: RowCellValues;
    indented?: boolean;
}

const formatPercentOfTotal = (value: number | null | undefined, total: number | null | undefined): string => {
    if (!value || !total) {
        return '';
    }
    const pct = (value / total) * 100;
    return `${pct.toFixed(1).replace(/\.0$/, '')}%`;
};

const SelectExecuteLoiPopup = (props: SelectExecuteLoiPopupProps) => {
    const { isVisible, onClose, item, levelOfService, engagementId, eventId, onSuccess } = props;
    const [stage, setStage] = useState<PopupStage>('confirming');
    const [isLoading, setIsLoading] = useState(false);
    const axios = useStateSelector((state) => state.core.axios);

    const showFairValue = levelOfService === LevelOfService.FullServiceBrokerage;

    const handleConfirm = async () => {
        setIsLoading(true);
        try {
            await selectAndExecuteLoi(axios, engagementId, eventId, item.buyerId);
            onSuccess();
            setStage('success');
        } catch {
            createNotification('Failed to select LOI. Please try again.', 'error');
        } finally {
            setIsLoading(false);
        }
    };

    const summary = item.form?.termForm?.purchasePriceSummary;
    const totalSellerFees = item.vvConnectFees?.netFeePaymentAtClosing?.fee ?? null;
    const totals = computeTotalValues(summary, totalSellerFees);
    const totalFaceValue = totals.faceValue ?? 0;
    const totalFairValue = totals.fairValue ?? 0;

    const priceRows: PriceSummaryRowData[] = [
        { label: 'Cash at Close', cells: buildCashAtCloseRow(item, totalSellerFees) },
        { label: 'Deferred Fixed Payments', cells: buildDeferredFixedRow(item) },
        { label: 'Deferred Variable Payments', cells: buildDeferredVariableRow(item) },
        { label: 'Earnout', cells: buildEarnoutRow(item), indented: true },
        { label: 'Synthetic JV Equity', cells: buildSyntheticJvRow(item), indented: true },
        { label: 'JV Equity', cells: buildJvEquityRow(item) },
        { label: 'Corporate Equity', cells: buildCorporateEquityRow(item) },
        { label: 'Other', cells: buildOtherRow(item) },
    ];

    const renderSummaryTable = () => (
        <table className="select-execute-loi-popup__table">
            <thead>
                <tr>
                    <th></th>
                    <th>Face Values</th>
                    <th>% of Total</th>
                    {showFairValue && (
                        <>
                            <th>Fair Value</th>
                            <th>% of Total</th>
                        </>
                    )}
                    <th>Fees</th>
                    <th>Net of Fees</th>
                </tr>
            </thead>
            <tbody>
                {priceRows.map((row) => (
                    <tr key={row.label}>
                        <td className={row.indented ? 'select-execute-loi-popup__row-label--indented' : undefined}>
                            {row.label}
                        </td>
                        <td>{formatCurrency(row.cells.faceValue)}</td>
                        <td>{formatPercentOfTotal(row.cells.faceValue, totalFaceValue)}</td>
                        {showFairValue && (
                            <>
                                <td>{formatCurrency(row.cells.fairValue)}</td>
                                <td>{formatPercentOfTotal(row.cells.fairValue, totalFairValue)}</td>
                            </>
                        )}
                        <td>{formatCurrencyAllowZero(row.cells.fees)}</td>
                        <td>{formatCurrencyAllowZero(row.cells.netOfFees)}</td>
                    </tr>
                ))}
                <tr className="total-row">
                    <td>Total Value</td>
                    <td>{formatCurrency(totals.faceValue)}</td>
                    <td></td>
                    {showFairValue && (
                        <>
                            <td>{formatCurrency(totals.fairValue)}</td>
                            <td></td>
                        </>
                    )}
                    <td>{formatCurrencyAllowZero(totals.fees)}</td>
                    <td>{formatCurrencyAllowZero(totals.netOfFees)}</td>
                </tr>
            </tbody>
        </table>
    );

    const renderConfirmingStage = () => (
        <ModalBody>
            <p>
                Are you sure that you want to accept the following offer from {item.buyerName}.
            </p>
            {renderSummaryTable()}
            <div className="mt-4 d-flex justify-content-end buttons-container">
                <button
                    className="btn btn-outline-secondary mr-2"
                    type="button"
                    disabled={isLoading}
                    onClick={onClose}>
                    Cancel
                </button>
                <ButtonLoader
                    className="btn btn-primary"
                    buttonText="Confirm"
                    loaderButtonText="Confirming..."
                    isLoading={isLoading}
                    disabled={false}
                    onClick={handleConfirm}
                />
            </div>
        </ModalBody>
    );

    const renderSuccessStage = () => (
        <ModalBody>
            <p>We will make the LOI available for your signature within 24 hours.</p>
            <div className="mt-4 d-flex justify-content-end buttons-container">
                <button
                    className="btn btn-primary"
                    type="button"
                    onClick={onClose}>
                    Yes, OK
                </button>
            </div>
        </ModalBody>
    );

    return (
        isVisible && (
            <Rodal
                visible={true}
                animation="slideTop"
                showCloseButton={false}
                width={POPUP_WIDTH}
                onClose={onClose}>
                <ModalHeader>
                    <b>Select &amp; Execute LOI</b>
                </ModalHeader>
                {stage === 'confirming' ? renderConfirmingStage() : renderSuccessStage()}
            </Rodal>
        )
    );
};

export default SelectExecuteLoiPopup;
